import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../../../core/i18n/translation.service';
import type { BranchDdlDto } from '../../../../../../core/models/branches/branch-ddl.dto';
import type { LookupGuidDto } from '../../../../../../core/models/common/lookup-guid.dto';
import type {
  RecruitmentEmailAccountDto,
  RecruitmentEmailTemplateDto,
} from '../../../../../../core/models/recruitment/recruitment.dto';
import type { UsersListDomainDto } from '../../../../../../core/models/users/users-list-domain.dto';
import { BranchesService } from '../../../../../../core/services/branches.service';
import { DepartmentsService } from '../../../../../../core/services/departments.service';
import { PermissionService } from '../../../../../../core/services/permission.service';
import { PositionsService } from '../../../../../../core/services/positions.service';
import { RecruitmentService } from '../../../../../../core/services/recruitment.service';
import { UsersService } from '../../../../../../core/services/users.service';
import { resetOnLeavingRecruitment } from './recruitment-scope';

/**
 * Recruitment-wide context: permission flags and the lookup catalogs every area needs (branches, departments,
 * positions, users, mailboxes, email templates). Provided on the `recruitment` parent route and reset whenever a
 * navigation ends outside Recruitment, so it lives while the user stays inside Recruitment and starts fresh on the
 * next visit.
 */
@Injectable()
export class RecruitmentContextStore {
  private readonly permissions = inject(PermissionService);
  private readonly recruitment = inject(RecruitmentService);
  private readonly branchesApi = inject(BranchesService);
  private readonly departmentsApi = inject(DepartmentsService);
  private readonly positionsApi = inject(PositionsService);
  private readonly usersApi = inject(UsersService);
  private readonly translation = inject(Translation);

  // --- Permissions ---

  readonly canManageHiringRequests = computed(() =>
    this.hasAny(['Recruitment.HiringRequests.Create', 'Recruitment.Manage']),
  );
  readonly canViewHiringRequests = computed(() =>
    this.hasAny([
      'Recruitment.View',
      'Recruitment.Manage',
      'Recruitment.HiringRequests.Create',
      'Recruitment.HiringRequests.Approve',
    ]),
  );
  readonly canManageRequisitions = computed(() =>
    this.hasAny(['Recruitment.Requisitions.Manage', 'Recruitment.Manage']),
  );
  readonly canApproveHiringRequests = computed(() =>
    this.hasAny(['Recruitment.HiringRequests.Approve', 'Recruitment.Manage']),
  );
  readonly canViewRequisitions = computed(() =>
    this.hasAny([
      'Recruitment.View',
      'Recruitment.Manage',
      'Recruitment.Requisitions.Manage',
    ]),
  );
  readonly canManageSettings = computed(() =>
    this.hasAny(['Recruitment.Settings.Manage', 'Recruitment.Manage']),
  );
  readonly canManageEmailAccounts = computed(() =>
    this.hasAny(['Recruitment.EmailAccounts.Manage', 'Recruitment.Manage']),
  );
  readonly canManageEmailTemplates = computed(() =>
    this.hasAny(['Recruitment.EmailTemplates.Manage', 'Recruitment.Manage']),
  );
  readonly canSendEmail = computed(() =>
    this.hasAny(['Recruitment.Email.Send', 'Recruitment.Manage']),
  );
  readonly canViewEmailInbox = computed(() =>
    this.hasAny(['Recruitment.EmailInbox.View', 'Recruitment.Manage']),
  );
  readonly canManageEmailInbox = computed(() =>
    this.hasAny(['Recruitment.EmailInbox.Manage', 'Recruitment.Manage']),
  );
  /**
   * create-candidate-from-email is guarded by a single backend permission (no "any" list and no
   * Recruitment.Manage umbrella), so the button is shown for exactly that permission — offering it
   * to a Manage-only role would only produce a 403.
   */
  readonly canImportCandidateFromEmail = computed(() =>
    this.hasAny(['Recruitment.Candidates.ImportFromEmail']),
  );
  readonly canLinkEmailToCandidate = computed(
    () => this.canManageEmailInbox() || this.canImportCandidateFromEmail(),
  );
  readonly canViewCandidates = computed(() =>
    this.hasAny(['Recruitment.View', 'Recruitment.Manage', 'Recruitment.Candidates.View', 'Recruitment.Candidates.Manage']),
  );
  readonly canManageCandidates = computed(() =>
    this.hasAny(['Recruitment.Candidates.Manage', 'Recruitment.Manage']),
  );
  /**
   * SP5a triage. These three deliberately do **not** reuse `canManageCandidates` / `canManageApplications`, which
   * treat `Recruitment.Manage` as an umbrella. Permission expansion runs upwards only — a child implies its
   * parents, never the reverse (`expandPermissionStrings`, and `PermissionAuthorizationHelper` server-side) — and
   * the triage endpoints are guarded by `PermissionAuthorize`, which requires **every** listed permission
   * literally. Offering a `Recruitment.Manage`-only role a button that can only 403 is the "disabled-and-lying"
   * failure in its other form, so these match what the controller actually demands, permission for permission.
   */
  readonly canTriageCreateCandidate = computed(() => this.hasAny(['Recruitment.Candidates.Manage']));
  readonly canTriageCreateCandidateAndApply = computed(() =>
    this.hasAll(['Recruitment.Candidates.Manage', 'Recruitment.Applications.Manage']),
  );
  readonly canTriageDismiss = computed(() => this.hasAny(['Recruitment.EmailInbox.Manage']));

  readonly canViewApplications = computed(() =>
    this.hasAny(['Recruitment.View', 'Recruitment.Manage', 'Recruitment.Applications.Manage']),
  );
  readonly canManageApplications = computed(() =>
    this.hasAny(['Recruitment.Applications.Manage', 'Recruitment.Manage']),
  );
  readonly canViewInterviews = computed(() =>
    this.hasAny(['Recruitment.View', 'Recruitment.Manage', 'Recruitment.Interviews.Manage']),
  );
  readonly canManageInterviews = computed(() =>
    this.hasAny(['Recruitment.Interviews.Manage', 'Recruitment.Manage']),
  );
  readonly canViewOffers = computed(() =>
    this.hasAny(['Recruitment.View', 'Recruitment.Manage', 'Recruitment.Offers.Manage']),
  );
  readonly canManageOffers = computed(() =>
    this.hasAny(['Recruitment.Offers.Manage', 'Recruitment.Manage']),
  );
  readonly canManageStages = computed(() =>
    this.hasAny(['Recruitment.Settings.Manage', 'Recruitment.Manage']),
  );
  /**
   * SP5f. The template read endpoints accept the recruitment read permission and `Requisitions.Manage` as well as
   * the settings permissions, deliberately: the requisition editor's picker and a requisition's "questions" cell
   * both resolve a template's name, and a list that comes back empty for permission reasons reads as "no templates
   * exist" rather than "you may not see them".
   */
  readonly canViewQuestionTemplates = computed(() =>
    this.hasAny([
      'Recruitment.View',
      'Recruitment.Manage',
      'Recruitment.Settings.Manage',
      'Recruitment.Requisitions.Manage',
    ]),
  );
  /** Create, edit and delete a template — the settings permissions only, matching the controller's write attributes. */
  readonly canManageQuestionTemplates = computed(() =>
    this.hasAny(['Recruitment.Settings.Manage', 'Recruitment.Manage']),
  );
  readonly canViewPipeline = computed(() =>
    this.hasAny([
      'Recruitment.View',
      'Recruitment.Manage',
      'Recruitment.Pipeline.View',
      'Recruitment.Pipeline.Manage',
      'Recruitment.Pipeline.Evaluate',
      'Recruitment.Pipeline.Statuses.Manage',
    ]),
  );
  readonly canManagePipeline = computed(() =>
    this.hasAny(['Recruitment.Pipeline.Manage', 'Recruitment.Manage']),
  );
  readonly canEvaluatePipeline = computed(() =>
    this.hasAny(['Recruitment.Pipeline.Evaluate', 'Recruitment.Pipeline.Manage', 'Recruitment.Manage']),
  );
  readonly canManagePipelineStatuses = computed(() =>
    this.hasAny(['Recruitment.Pipeline.Statuses.Manage', 'Recruitment.Pipeline.Manage', 'Recruitment.Manage']),
  );

  // --- Catalogs ---

  private readonly branchesState = signal<BranchDdlDto[]>([]);
  private readonly departmentsState = signal<LookupGuidDto[]>([]);
  private readonly positionsState = signal<LookupGuidDto[]>([]);
  private readonly usersState = signal<UsersListDomainDto[]>([]);
  private readonly emailAccountsState = signal<RecruitmentEmailAccountDto[]>([]);
  private readonly emailTemplatesState = signal<RecruitmentEmailTemplateDto[]>([]);
  private readonly loadedState = signal(false);
  private readonly loadErrorState = signal<string | null>(null);

  readonly branches = this.branchesState.asReadonly();
  readonly departments = this.departmentsState.asReadonly();
  readonly positions = this.positionsState.asReadonly();
  readonly users = this.usersState.asReadonly();
  readonly emailAccounts = this.emailAccountsState.asReadonly();
  readonly emailTemplates = this.emailTemplatesState.asReadonly();
  readonly loaded = this.loadedState.asReadonly();
  readonly loadError = this.loadErrorState.asReadonly();

  /**
   * Bumped when the user leaves Recruitment. `PermissionService` reads storage (not signals), so the permission
   * computeds read this to be re-evaluated on the next visit, and responses started before a reset are dropped.
   */
  private readonly visit = signal(0);
  private loadInFlight: Promise<void> | null = null;

  constructor() {
    resetOnLeavingRecruitment(() => this.reset());
  }

  /**
   * Loads every catalog in one `forkJoin`, once. Each lookup falls back to an empty list on failure, and the mailbox
   * and template lists are only requested for users who can send email or manage them. Concurrent callers share the
   * in-flight request; after a successful load it resolves immediately.
   */
  ensureLoaded(): Promise<void> {
    if (this.loadedState()) return Promise.resolve();
    if (this.loadInFlight) return this.loadInFlight;

    const visit = untracked(this.visit);
    untracked(() => this.loadErrorState.set(null));
    const request = new Promise<void>((resolve) => {
      forkJoin({
        branches: this.branchesApi.getBranchesDdl().pipe(catchError(() => of([] as BranchDdlDto[]))),
        departments: this.departmentsApi.getDepartmentsDdl().pipe(catchError(() => of([] as LookupGuidDto[]))),
        positions: this.positionsApi.getPositionDdl().pipe(catchError(() => of([] as LookupGuidDto[]))),
        users: this.usersApi.getUsersListDDL({ rowCount: 200 }).pipe(catchError(() => of([] as UsersListDomainDto[]))),
        emailAccounts: this.canSendEmail() || this.canManageEmailAccounts()
          ? this.recruitment.emailAccounts().pipe(catchError(() => of([] as RecruitmentEmailAccountDto[])))
          : of([] as RecruitmentEmailAccountDto[]),
        emailTemplates: this.canSendEmail() || this.canManageEmailTemplates()
          ? this.recruitment.emailTemplates().pipe(catchError(() => of([] as RecruitmentEmailTemplateDto[])))
          : of([] as RecruitmentEmailTemplateDto[]),
      }).subscribe({
        next: ({ branches, departments, positions, users, emailAccounts, emailTemplates }) => {
          if (this.isStale(visit)) return;
          this.branchesState.set(branches ?? []);
          this.departmentsState.set(departments ?? []);
          this.positionsState.set(positions ?? []);
          this.usersState.set(users ?? []);
          this.emailAccountsState.set(emailAccounts ?? []);
          this.emailTemplatesState.set(emailTemplates ?? []);
          this.loadedState.set(true);
        },
        error: (err: unknown) => {
          if (!this.isStale(visit)) {
            this.loadErrorState.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
          }
          resolve();
        },
        complete: () => resolve(),
      });
    });
    this.loadInFlight = request;
    void request.then(() => {
      if (this.loadInFlight === request) this.loadInFlight = null;
    });
    return request;
  }

  /** Re-reads the mailbox list. Rejects with the request error so the caller can surface it (as the areas do today). */
  refreshEmailAccounts(): Promise<void> {
    const visit = untracked(this.visit);
    return new Promise<void>((resolve, reject) => {
      this.recruitment.emailAccounts().subscribe({
        next: (rows) => {
          if (!this.isStale(visit)) this.emailAccountsState.set(rows ?? []);
        },
        error: (err: unknown) => reject(err),
        complete: () => resolve(),
      });
    });
  }

  /** Re-reads the email template list. Rejects with the request error so the caller can surface it. */
  refreshEmailTemplates(): Promise<void> {
    const visit = untracked(this.visit);
    return new Promise<void>((resolve, reject) => {
      this.recruitment.emailTemplates().subscribe({
        next: (rows) => {
          if (!this.isStale(visit)) this.emailTemplatesState.set(rows ?? []);
        },
        error: (err: unknown) => reject(err),
        complete: () => resolve(),
      });
    });
  }

  /** Forgets every catalog and re-evaluates the permission flags on next read (the user left Recruitment). */
  reset(): void {
    this.visit.update((value) => value + 1);
    this.loadInFlight = null;
    this.branchesState.set([]);
    this.departmentsState.set([]);
    this.positionsState.set([]);
    this.usersState.set([]);
    this.emailAccountsState.set([]);
    this.emailTemplatesState.set([]);
    this.loadErrorState.set(null);
    this.loadedState.set(false);
  }

  private hasAny(required: readonly string[]): boolean {
    this.visit();
    return this.permissions.hasAny(required);
  }

  /** Every one of them, for an endpoint guarded by `PermissionAuthorize` rather than `PermissionAuthorizeAny`. */
  private hasAll(required: readonly string[]): boolean {
    this.visit();
    return this.permissions.hasAll(required);
  }

  private isStale(visit: number): boolean {
    return untracked(this.visit) !== visit;
  }
}
